import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { passwordResetRequest } from "@/lib/api";

export default function ForgotPassword() {
  const { tr } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await passwordResetRequest(email);
      // Always show the same generic confirmation regardless of whether
      // the address exists (no user-enumeration; no link ever displayed).
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        toast.error(detail || "Too many requests");
      } else {
        toast.error(detail || "Request failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell hideNav>
      <section
        className="px-5 pt-6 pb-10 space-y-5"
        data-testid="forgot-password-screen"
      >
        <h1
          className="text-2xl font-bold text-[rgb(var(--wpe-navy))]"
          data-testid="forgot-password-title"
        >
          {tr("forgotPasswordTitle")}
        </h1>
        <p className="text-[13px] text-[rgb(var(--wpe-slate))]">
          {tr("forgotPasswordSub")}
        </p>

        {!submitted ? (
          <form
            onSubmit={onSubmit}
            className="wpe-card space-y-4"
            data-testid="forgot-password-form"
          >
            <label className="block">
              <span className="text-[12px] font-semibold text-[rgb(var(--wpe-slate))] block mb-1">
                {tr("email")}
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                className="wpe-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email-input"
              />
            </label>
            <button
              type="submit"
              className="wpe-cta w-full"
              disabled={submitting}
              data-testid="forgot-password-submit-btn"
            >
              {submitting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              <span>{tr("sendResetLink")}</span>
            </button>
          </form>
        ) : (
          <div
            className="wpe-card space-y-2"
            data-testid="forgot-password-result"
          >
            <div
              className="text-[13px] text-[rgb(var(--wpe-navy))]"
              data-testid="forgot-password-sent-msg"
            >
              {tr("resetLinkSent")}
            </div>
            <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
              {tr("forgotPasswordAdminHint")}
            </div>
          </div>
        )}

        <Link
          to="/auth"
          className="wpe-ghost w-full text-[13px] justify-center"
          data-testid="forgot-password-back-link"
        >
          {tr("backToSignIn")}
        </Link>

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
